import type { Configuration, PublicClientApplication } from "@azure/msal-browser";
import { isClient } from "./browser";

export interface AppleAuthConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  state: string;
  usePopup: boolean;
}

/** Apple JS SDK (Sign in with Apple). */
export const APPLE_SSO_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js" as const;

export const getAppleSSOConfig = (): AppleAuthConfig => {
  const clientId = process.env.NEXT_PUBLIC_APPLE_CLIENT_ID;
  if (!clientId) {
    throw new Error("Missing Apple SSO configuration");
  }

  const currentHost = isClient() ? window.location.host : "qrcreator.com";
  // Always HTTPS by Apple requirement
  const redirectURI = `https://${currentHost}/auth/apple/callback`;

  return {
    clientId: clientId,
    scope: "name email",
    redirectURI,
    state: `origin:${currentHost.replace(/\./g, "_")}`,
    usePopup: true,
  };
};

/**
 * Microsoft authority. `/common` allows both personal
 * and corporate Azure AD accounts.
 */
export const MICROSOFT_SSO_AUTHORITY = "https://login.microsoftonline.com/common" as const;

/**
 * MSAL v5 redirect bridge route. The page at this path must call
 * `broadcastResponseToMainFrame()` from `@azure/msal-browser/redirect-bridge`
 * so the popup can hand the auth response back to the parent window via
 * BroadcastChannel
 *
 * The route is excluded from i18n locale prefixing in `I18nMiddleware` to
 * keep the popup URL stable across locales (and matches the redirect URI
 * registered in the Entra ID app registration).
 */
export const MICROSOFT_SSO_REDIRECT_PATH = "/auth/redirect" as const;

/**
 * Builds the MSAL `Configuration` for `PublicClientApplication` with a
 * dynamic `redirectUri` resolved at runtime, so the SSO flow returns the
 * user to the originating domain (dev, staging, prod or any white-labelled
 * site). The function is intended to be invoked exclusively from a button
 * click handler — never during SSR or module initialization — and will
 * throw if called outside a browser context to prevent the SSO flow from
 * silently redirecting users to an incorrect domain.
 */
export const getMicrosoftConfig = (): Configuration => {
  const clientId = process.env.NEXT_PUBLIC_MS_CLIENT_ID;
  if (!clientId) {
    throw new Error("Missing Microsoft SSO configuration");
  }

  if (!isClient()) {
    throw new Error("getMicrosoftConfig must be called from a browser context");
  }

  return {
    auth: {
      clientId,
      authority: MICROSOFT_SSO_AUTHORITY,
      redirectUri: `${window.location.origin}${MICROSOFT_SSO_REDIRECT_PATH}`,
    },
    system: {
      popupBridgeTimeout: 15000,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  };
};

/**
 * Lazily-created singleton `PublicClientApplication`
 */
let microsoftPublicClient: PublicClientApplication | null = null;

/**
 * `PublicClientApplication` instantiating and
 * initializing it on first call. MSAL v5 requires `initialize()` to be
 * awaited before any login API is invoked, so callers must `await` this.
 *
 * Intended to be invoked from a button click handler, never on page load.
 */
export const getMicrosoftPublicClient = async (): Promise<PublicClientApplication> => {
  if (microsoftPublicClient) {
    return microsoftPublicClient;
  }

  const { PublicClientApplication } = await import("@azure/msal-browser");
  const instance = new PublicClientApplication(getMicrosoftConfig());
  await instance.initialize();
  microsoftPublicClient = instance;
  return microsoftPublicClient;
};

/**
 * Test-only reset of the cached `PublicClientApplication` singleton.
 * Allows unit tests to exercise the lazy-instantiation path repeatedly
 * without leaking state between cases.
 */
export const __resetMicrosoftPublicClientForTests = (): void => {
  microsoftPublicClient = null;
};
